// editQueue.ts
// Serializes cell edits per row so a slow edit (one that triggers backend reprocessing)
// doesn't block or drop edits elsewhere. Edits sharing a key run in order; different
// keys run in parallel. Key by the load being edited, e.g. `schedule_table:${rowData.id}`
// for schedule cells and their pickup/delivery/main_table sub-table edits.
import { useSyncExternalStore } from 'react';

const tails = new Map<string, Promise<unknown>>();
const pending = new Map<string, number>();
// Bumped by cancelQueue; queued edits from an older generation are skipped.
const generations = new Map<string, number>();
const listeners = new Set<() => void>();

const notify = () => listeners.forEach((listener) => listener());

const adjustPending = (key: string, delta: number) => {
    const count = (pending.get(key) || 0) + delta;
    if (count > 0) pending.set(key, count);
    else pending.delete(key);
    notify();
};

export class EditCancelledError extends Error {
    constructor() {
        super('Edit cancelled');
    }
}

// Runs fn after every edit already queued under key. Rejects with EditCancelledError
// if cancelQueue(key) is called before fn starts.
export const enqueueEdit = <T,>(key: string, fn: () => Promise<T>): Promise<T> => {
    const generation = generations.get(key) || 0;
    const previous = tails.get(key) || Promise.resolve();

    adjustPending(key, 1);
    const run = previous
        .catch(() => undefined)
        .then(() => {
            if ((generations.get(key) || 0) !== generation) throw new EditCancelledError();
            return fn();
        })
        .finally(() => {
            adjustPending(key, -1);
            if (tails.get(key) === tail) tails.delete(key);
        });

    const tail = run.catch(() => undefined);
    tails.set(key, tail);
    return run;
};

// Drops edits queued under key that haven't started yet (e.g. the row got locked by another user).
export const cancelQueue = (key: string) => {
    generations.set(key, (generations.get(key) || 0) + 1);
};

const subscribe = (listener: () => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
};

// Number of edits queued or running under key.
export const usePendingEdits = (key: string) =>
    useSyncExternalStore(subscribe, () => pending.get(key) || 0);
